import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Upload, IndianRupee, Check } from "lucide-react";

export default function Register() {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ tehsils: [], roles: [], jerseys: [] });
  const [settings, setSettings] = useState({});
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", mobile: "", age: "", role: "", jersey_size: "", tehsil: "", password: "", confirm_password: "" });
  const [player, setPlayer] = useState(null);
  const [utr, setUtr] = useState("");
  const [screenshotId, setScreenshotId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payElapsed, setPayElapsed] = useState(0);

  const PAY_MIN_UTR = 12;
  const PAY_MIN_WAIT = 60;
  const payReady = !!screenshotId && utr.trim().length >= PAY_MIN_UTR && payElapsed >= PAY_MIN_WAIT;
  const payRemaining = Math.max(0, PAY_MIN_WAIT - payElapsed);
  const PAY_INCOMPLETE_MSG = "Please complete your payment first, upload a valid payment screenshot, and enter the UTR before submitting.";

  useEffect(() => {
    if (step !== 2) return;
    setPayElapsed(0);
    const iv = setInterval(() => setPayElapsed((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [step]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const submitRegistration = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.age || !form.role || !form.jersey_size || !form.tehsil || !form.password || !form.confirm_password) {
      toast.error("Please fill all fields"); return;
    }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm_password) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const { confirm_password, ...payload } = form;
      const res = await api.post("/players/register", { ...payload, age: parseInt(payload.age) });
      localStorage.setItem("ndpl_player_token", res.data.token);
      setPlayer(res.data.player);
      setStep(2);
      toast.success("Registered! Complete payment to proceed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setSubmitting(false);
  };

  const uploadScreenshot = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "payments");
    try {
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScreenshotId(res.data.file_id);
      toast.success("Screenshot uploaded");
    } catch (err) { toast.error("Upload failed"); }
    setUploading(false);
  };

  const submitPayment = async () => {
    if (!payReady) { toast.error(PAY_INCOMPLETE_MSG); return; }
    setSubmitting(true);
    try {
      await api.post("/players/me/payment", { utr, screenshot_id: screenshotId });
      toast.success("Payment submitted for verification");
      setStep(3);
    } catch (err) { toast.error("Failed to submit payment"); }
    setSubmitting(false);
  };

  const Field = ({ label, name, type = "text" }) => (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60">{label}</span>
      <input data-testid={`reg-${name}`} type={type} value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-4 py-3 text-white" />
    </label>
  );
  const Selector = ({ label, name, options }) => (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60">{label}</span>
      <select data-testid={`reg-${name}`} value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-4 py-3 text-white">
        <option value="">Select {label}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0B132B]">{o}</option>)}
      </select>
    </label>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">
        <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Player Registration</div>
        <h1 className="font-display text-5xl md:text-6xl mt-3">Join <span className="text-[#FFD700]">NDPL 2026</span></h1>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-2 sm:gap-3 flex-wrap">
          {[{ n: 1, l: "Details" }, { n: 2, l: "Payment" }, { n: 3, l: "Done" }].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 grid place-items-center border-2 ${step >= s.n ? "border-[#39FF14] bg-[#39FF14] text-black" : "border-white/20 text-white/40"} font-display`}>{s.n}</div>
              <div className={`text-xs uppercase tracking-widest ${step >= s.n ? "text-white" : "text-white/40"}`}>{s.l}</div>
              {i < 2 && <div className="w-4 sm:w-8 h-px bg-white/20" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={submitRegistration} className="mt-10 space-y-5 p-6 md:p-8 bg-[#0B132B] border border-white/10 rounded-lg">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Player Name" name="name" />
              <Field label="Mobile Number" name="mobile" />
              <Field label="Age" name="age" type="number" />
              <Selector label="Playing Role" name="role" options={meta.roles} />
              <Selector label="Jersey Size" name="jersey_size" options={meta.jerseys} />
              <Selector label="Tehsil" name="tehsil" options={meta.tehsils} />
              <Field label="Create Password (min 6 chars)" name="password" type="password" />
              <Field label="Confirm Password" name="confirm_password" type="password" />
            </div>
            <div className="pt-4 flex items-center justify-between">
              <div className="text-sm text-white/60">Registration fee: <span className="text-[#FFD700] font-bold">₹{settings.registration_fee || 799}</span></div>
              <button data-testid="reg-submit" disabled={submitting} className="px-8 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest disabled:opacity-50">{submitting ? "..." : "Continue to Payment"}</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#0B132B] border border-[#FFD700]/30 rounded-lg text-center">
              <IndianRupee className="text-[#FFD700] mx-auto" />
              <div className="font-display text-5xl text-[#FFD700] mt-2">₹{settings.registration_fee || 799}</div>
              <div className="mt-4 p-4 bg-black/40 border border-white/10">
                {settings.upi_qr_file_id ? (
                  <img src={fileUrl(settings.upi_qr_file_id)} alt="UPI QR" className="w-48 h-48 mx-auto object-contain" />
                ) : (
                  <div className="w-48 h-48 mx-auto grid place-items-center border border-dashed border-white/20 text-white/40 text-xs">QR PLACEHOLDER<br/>Set QR in Admin Settings</div>
                )}
              </div>
              <div className="mt-4 text-xs uppercase tracking-widest text-white/60">UPI ID</div>
              <div className="font-display text-2xl text-[#39FF14]" data-testid="upi-id">{settings.upi_id || "ndpl@upi"}</div>
              <a href={`upi://pay?pa=${settings.upi_id}&pn=NDPL&am=${settings.registration_fee || 799}&cu=INR`} className="mt-5 inline-block px-6 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest">Pay via UPI</a>
            </div>
            <div className="p-6 bg-[#0B132B] border border-white/10 rounded-lg space-y-4">
              <div className="text-xs uppercase tracking-[0.3em] text-[#39FF14]">After Payment</div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60">UTR / Transaction ID</span>
                <input data-testid="pay-utr" value={utr} onChange={(e) => setUtr(e.target.value)} className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-4 py-3" placeholder="e.g. 123456789012" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60">Payment Screenshot</span>
                <div className="mt-2 flex items-center gap-3">
                  <label className="flex-1 flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer hover:border-[#39FF14]">
                    <Upload size={16} className="text-[#39FF14]" />
                    <span className="text-sm text-white/70 truncate">{screenshotId ? "Uploaded ✓" : "Choose image"}</span>
                    <input data-testid="pay-screenshot" type="file" accept="image/*" className="hidden" onChange={(e) => uploadScreenshot(e.target.files?.[0])} />
                  </label>
                  {uploading && <span className="text-xs text-white/50">Uploading...</span>}
                </div>
              </label>
              <button data-testid="pay-submit" onClick={submitPayment} disabled={submitting || !payReady} className="w-full py-3 bg-[#FFD700] text-black font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed">
                {payRemaining > 0 ? `Wait ${payRemaining}s to submit` : "Submit for Verification"}
              </button>
              {!payReady && <div className="text-xs text-white/50">Complete payment, upload screenshot, enter UTR (min 12 chars){payRemaining > 0 ? `, and wait ${payRemaining}s` : ""} to enable submit.</div>}
              <div className="text-xs text-white/50">Registration remains <span className="text-[#FFD700]">Pending</span> until Admin verifies your payment.</div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-10 p-10 bg-[#0B132B] border border-[#39FF14]/30 rounded-lg text-center">
            <div className="w-16 h-16 rounded-full bg-[#39FF14] text-black grid place-items-center mx-auto"><Check size={32} /></div>
            <h2 className="font-display text-4xl mt-5">Payment Submitted</h2>
            <p className="text-white/70 mt-3">You will be notified once Admin verifies your payment.</p>
            <button data-testid="goto-dashboard" onClick={() => nav("/player/dashboard")} className="mt-6 px-8 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest">Go to Dashboard</button>
          </div>
        )}
      </div>
      <Footer settings={settings} />
      <FloatingWhatsapp />
    </div>
  );
}
